import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardBody,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  VStack,
  Text,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  Badge,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Icon,
  Center,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputLeftElement,
  HStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, SearchIcon, DownloadIcon } from "@chakra-ui/icons";
import { get_all_customer_list } from "../../redux/customerSlice";
import { get_loan_list } from "../../redux/LoanSlice";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const LoanListComponent = () => {
  const dispatch = useDispatch();
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedCustomerName, setSelectedCustomerName] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [excelUrl, setExcelUrl] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [totalSecurityDeposit, setTotalSecurityDeposit] = useState(0);

  const { all_customer_list, all_loading } = useSelector(
    (state) => state.customers
  );
  const { loanList, loading } = useSelector((state) => state.loan);

  // Fetch all customers on mount
  useEffect(() => {
    dispatch(get_all_customer_list({ page: 1, rows: 10000 }));
  }, [dispatch]);

  const handleSearch = () => {
    if (selectedCustomer) {
      dispatch(get_loan_list({ userid: selectedCustomer, action: "loan_list" }))
        .then((response) => {
          // Set the URLs from the API response
          if (response.payload && response.payload.excel_url) {
            setExcelUrl(response.payload.excel_url);
          }
          if (response.payload && response.payload.pdf_url) {
            setPdfUrl(response.payload.pdf_url);
          }
          if (response.payload && response.payload.total_security_deposit) {
            setTotalSecurityDeposit(response.payload.total_security_deposit);
          }
        });
      setShowResults(true);
    } else {
      alert("Please select a customer first");
    }
  };

  // Filter customers by name while typing in dropdown
  const filteredCustomers = all_customer_list.filter((c) =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Download Excel file
  const handleDownloadExcel = () => {
    if (excelUrl) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = excelUrl;
      link.setAttribute('download', 'Loan_Report.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert("Excel file not available");
    }
  };

  const calculateProductTotals = (orders) => {
    return orders.reduce(
      (acc, order) => {
        const count = Number(order.count) || 0;
        const deliveredQty = Number(order.delivered_quantity) || 0;
        const emptyReceived = Number(order.empty_received) || 0;
        const equipmentUsed = Number(order.equipment_used_qty) || 0;
        const sdCount = Number(order.sd_count) || 0;
        const securityDepositPerUnit = Number(order.security_deposit_per_unit) || 0;
        const securityTotal = securityDepositPerUnit * sdCount; // ✅ Correct total

        return {
          totalCount: acc.totalCount + count,
          totalDelivered: acc.totalDelivered + deliveredQty,
          totalEmptyReceived: acc.totalEmptyReceived + emptyReceived,
          totalEquipmentUsed: acc.totalEquipmentUsed + equipmentUsed,
          loanBalance: acc.loanBalance + (deliveredQty - emptyReceived),
          totalSdCount: acc.totalSdCount + sdCount,
          totalSecurity: acc.totalSecurity + securityTotal, // ✅ accumulate total
        };
      },
      {
        totalCount: 0,
        totalDelivered: 0,
        totalEmptyReceived: 0,
        totalEquipmentUsed: 0,
        loanBalance: 0,
        totalSdCount: 0,
        totalSecurity: 0, // ✅ add initial key
      }
    );
  };


  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        {/* Header with Download Buttons */}
        <Flex justify="space-between" align="center" wrap="wrap">
          <Heading size="lg" color="gray.700">
            Loan Details
          </Heading>

          {/* Download Buttons - Only show when we have data */}
          {excelUrl && (
            <HStack spacing={3}>
              <Button
                onClick={handleDownloadExcel}
                colorScheme="green"
                size="md"
                leftIcon={<DownloadIcon />}
                variant="solid"
              >
                Download Excel
              </Button>
            </HStack>
          )}
        </Flex>

        {/* Search Section */}
        <Card w="50%">
  <CardBody>
    <Flex gap={4} align="flex-end" flexWrap="wrap">
      <FormControl flex={1}>
        <FormLabel>Select Customer</FormLabel>

        {/* Searchable Dropdown */}
        <Menu closeOnSelect={true} matchWidth>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w="100%"
            size="lg"
            variant="outline"
            isDisabled={all_loading}
            textAlign="left"
          >
            {selectedCustomerName || "Select Customer"}
          </MenuButton>

          <MenuList maxH="250px" overflowY="auto" p={2}>
            {/* Search Input Inside Dropdown */}
            <InputGroup mb={2}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                autoFocus
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </InputGroup>

            {filteredCustomers.length > 0 ? (
              filteredCustomers.map((customer) => (
                <MenuItem
                  key={customer.id}
                  onClick={() => {
                    setSelectedCustomer(customer.id);
                    setSelectedCustomerName(customer.name);
                    setSearchTerm("");
                    // Reset URLs when customer changes
                    setExcelUrl("");
                    setPdfUrl("");
                  }}
                >
                  <Box>
                    <Text fontWeight="medium">
                      {customer.name || customer.mobile}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {customer.mobile}
                      {customer.email && ` • ${customer.email}`}
                    </Text>
                  </Box>
                </MenuItem>
              ))
            ) : (
              <Box p={3} textAlign="center" color="gray.500">
                No customers found
              </Box>
            )}
          </MenuList>
        </Menu>
      </FormControl>

      <Button
        leftIcon={<SearchIcon />}
        colorScheme="blue"
        onClick={handleSearch}
        isDisabled={!selectedCustomer || loading}
        isLoading={loading}
        loadingText="Searching"
        size="lg"
      >
        Search
      </Button>
    </Flex>
  </CardBody>
</Card>


        {/* Display Total Security Deposit if available */}
        {/* {totalSecurityDeposit > 0 && (
          <Card>
            <CardBody>
              <Center>
                <Stat textAlign="center">
                  <StatLabel color="gray.600" fontSize="lg">
                    Total Security Deposit
                  </StatLabel>
                  <StatNumber color="blue.600" fontSize="3xl">
                    ₹{totalSecurityDeposit}
                  </StatNumber>
                </Stat>
              </Center>
            </CardBody>
          </Card>
        )} */}

        {/* Results Section */}
        {showResults && (
          <Box>
            {loading ? (
              <GasCylinderLoader
              size="sm"
              loadingText="Loan details loading"></GasCylinderLoader>
              
            ) : loanList && loanList.length > 0 ? (
              <VStack spacing={6} align="stretch">
                {loanList.map((product, index) => {
                  const totals = calculateProductTotals(product.orders);
                  return (
                    <Card key={index} variant="outline" borderWidth={2}>
                      <Box bg="blue.500" color="white" p={4} borderTopRadius="md">
                        <Heading size="md">{product.product_name}</Heading>
                        <Text fontSize="sm" opacity={0.9}>
                          {product.brand} - {product.productid}
                        </Text>
                      </Box>

                      <CardBody>
                        <Grid
                          templateColumns={{
                            base: "repeat(2, 1fr)",
                            md: "repeat(6, 1fr)",
                          }}
                          gap={4}
                          mb={6}
                          p={4}
                          bg="gray.50"
                          borderRadius="md"
                        >
                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Total Orders
                              </StatLabel>
                              <StatNumber color="gray.800" fontSize="2xl">
                                {totals.totalCount}
                              </StatNumber>
                            </Stat>
                          </GridItem>
                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Delivered
                              </StatLabel>
                              <StatNumber color="green.600" fontSize="2xl">
                                {totals.totalDelivered}
                              </StatNumber>
                            </Stat>
                          </GridItem>
                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Empties Received
                              </StatLabel>
                              <StatNumber color="blue.600" fontSize="2xl">
                                {totals.totalEmptyReceived}
                              </StatNumber>
                            </Stat>
                          </GridItem>
                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Loan Balance
                              </StatLabel>
                              <StatNumber
                                color={totals.loanBalance > 0 ? "red.600" : "green.600"}
                                fontSize="2xl"
                              >
                                {totals.loanBalance}
                              </StatNumber>
                            </Stat>
                          </GridItem>
                          {/* <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Equipment Used
                              </StatLabel>
                              <StatNumber color="purple.600" fontSize="2xl">
                                {totals.totalEquipmentUsed}
                              </StatNumber>
                            </Stat>
                          </GridItem> */}
                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Total SD Count
                              </StatLabel>
                              <StatNumber color="orange.600" fontSize="2xl">
                                {totals.totalSdCount}
                              </StatNumber>
                            </Stat>
                          </GridItem>

                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Total Security Deposit
                              </StatLabel>
                              <StatNumber color="green.600" fontSize="2xl">
                                ₹{totals.totalSecurity.toFixed(2)}
                              </StatNumber>
                            </Stat>
                          </GridItem>
                        </Grid>



                        {/* Orders Table */}
                        <Box overflowX="auto">
                          <Table variant="simple" size="sm">
                            <Thead bg="gray.100">
                              <Tr>
                                <Th>Order ID</Th>
                                <Th>Order Date</Th>
                                <Th isNumeric>Quantity</Th>
                                <Th isNumeric>Delivered</Th>
                                <Th isNumeric>Empty Received</Th>
                                <Th>Extra Picked Product</Th>
                                <Th isNumeric>Customer Owned Equipment</Th>
                                <Th isNumeric>Loan Equipment</Th>
                                <Th isNumeric>Sd Count</Th>
                                <Th isNumeric>Security Deposit</Th>
                                
                                <Th isNumeric>Balance</Th>
                                <Th>Delivery Date</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {product.orders.map((order, i) => {
                                const balance =
                                  Number(order.delivered_quantity) - Number(order.empty_received);
                                return (
                                  <Tr key={i} _hover={{ bg: "gray.50" }}>
                                    <Td>{order.orderid}</Td>
                                    <Td>
                                      {new Date(order.ordered_datetime).toLocaleString("en-IN")}
                                    </Td>
                                    <Td isNumeric>{order.count}</Td>
                                    <Td isNumeric color="green.600">
                                      {order.delivered_quantity}
                                    </Td>
                                    <Td isNumeric color="blue.600">{order.empty_received}</Td>
                                    <Td>{order.extra_product_name || "NA"}</Td>
                                    <Td isNumeric>{order.extra_received_empty}</Td>
                                    <Td isNumeric>{order.equipment_used_qty}</Td>
                                    <Td isNumeric>{order.sd_count || "NA"}</Td>
                                    {/* New Security Deposit Column */}
                                    <Td isNumeric color="purple.600">
                                      ₹{order.security_deposit_per_unit || "0.00"}
                                    </Td>
                                    
                                    <Td isNumeric>
                                      <Badge
                                        colorScheme={
                                          balance > 0 ? "red" : balance < 0 ? "green" : "gray"
                                        }
                                      >
                                        {balance}
                                      </Badge>
                                    </Td>
                                    <Td>
                                      {order.deliver_datetime ? (
                                        new Date(order.deliver_datetime).toLocaleString("en-IN")
                                      ) : (
                                        <Badge colorScheme="orange">Pending</Badge>
                                      )}
                                    </Td>
                                  </Tr>
                                );
                              })}
                            </Tbody>
                          </Table>
                        </Box>
                      </CardBody>
                    </Card> 
                  );
                })}
              </VStack>
            ) : (
              <Card>
                <CardBody>
                  <Center py={12}>
                    <VStack spacing={4}>
                      <Icon viewBox="0 0 24 24" boxSize={12} color="gray.400">
                        <path
                          fill="currentColor"
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                        />
                      </Icon>
                      <Heading size="md" color="gray.900">
                        No loan records found
                      </Heading>
                      <Text color="gray.500" fontSize="sm">
                        This customer doesn't have any loan records.
                      </Text>
                    </VStack>
                  </Center>
                </CardBody>
              </Card>
            )}
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default LoanListComponent;